import React, { FC, memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { ISite, ISiteSetupDTO, Site, SiteEquipment } from 'shared/api/api';
import { CreateSiteAndRegisterAsync } from 'shared/state/ducks/site/operations';
import { RemoveProcessEquipment } from 'shared/state/ducks/process-equipments/operations';
import { localized } from 'shared/state/i18n/i18n';
import { FlexRow } from '../basic-flex/flex-row';
import { SkioldButton } from '../skiold-components/skiold-button/skiold-button';
import {
	useEditContext,
	useEquipmentsContext,
	useOwnerContext,
	useProcessEquipmentContext,
	useSiteContext,
} from './create-site-context-provider';
import { MapCreateSiteFormValuesToSite } from './create-site-helpers';
import { ShowOkAlert } from '../skiold-alert/skiold-alert';

interface Props {}

export const CreateSiteButtons: FC<Props> = memo(() => {
	const owner = useOwnerContext();
	const site = useSiteContext();
	const processEquipment = useProcessEquipmentContext();
	const { equipments, clearEquipments, pendingDeleteCodes, clearPendingDeletes, equipmentCodeToId } = useEquipmentsContext();
	const { editingSiteId, stopEditing } = useEditContext();

	const dispatch = useDispatch();

	const clearAll = useCallback(() => {
		owner.clearFields();
		site.clearFields();
		processEquipment.clearFields();
		clearEquipments();
		clearPendingDeletes();
		stopEditing();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		owner.clearFields,
		site.clearFields,
		processEquipment.clearFields,
		clearEquipments,
		clearPendingDeletes,
		stopEditing,
	]);

	const validateSiteSetupInput = useCallback((siteSetup: ISiteSetupDTO) => {
		const target = siteSetup.site as ISite;
		const missingInputs: string[] = [];

		if (!target.siteName) missingInputs.push(localized('siteName'));
		if (!target.siteAddress) missingInputs.push(localized('siteAddress'));
		if (!target.siteZipCode) missingInputs.push(localized('siteZipCode'));
		if (!target.siteCity) missingInputs.push(localized('siteCity'));
		if (!target.countryId) missingInputs.push(localized('country'));
		if (!target.chRnum) missingInputs.push(localized('chRnum'));
		if (!target.ownerName) missingInputs.push(localized('ownerName'));
		if (!target.ownerAddress) missingInputs.push(localized('ownerAddress'));
		if (!target.ownerZipCode) missingInputs.push(localized('ownerZipCode'));
		if (!target.ownerCity) missingInputs.push(localized('ownerCity'));
		if (!target.cvRnum) missingInputs.push(localized('cvRnum'));

		if (missingInputs.length > 0) {
			ShowOkAlert(`${localized('FillOut')}:\n- ${missingInputs.join('\n- ')}`);
			return false;
		}

		return true;
	}, []);

	const handleSubmit = useCallback(async () => {
		const newSite = MapCreateSiteFormValuesToSite(owner.fields, site.fields, equipments.at(0));
		if (editingSiteId) {
			newSite.id = editingSiteId;
		}

		const siteSetup: ISiteSetupDTO = {
			site: newSite as Site,
			equipments: equipments as SiteEquipment[],
		};
		if (!validateSiteSetupInput(siteSetup)) return;

		if (editingSiteId) {
			const success = await CreateSiteAndRegisterAsync(siteSetup, true)(dispatch);

			if (success) {
				// When deleting process equipment we purely delete the equipment in the database and does not delete the edge modules on Azure.
				await Promise.all(
					pendingDeleteCodes
						.filter(code => equipmentCodeToId[code])
						.map(code => RemoveProcessEquipment(equipmentCodeToId[code])(dispatch)),
				);
				clearAll();
			}
		} else {
			const success = await CreateSiteAndRegisterAsync(siteSetup)(dispatch);
			if (success) clearAll();
		}
	}, [
		owner.fields,
		site.fields,
		equipments,
		editingSiteId,
		dispatch,
		validateSiteSetupInput,
		clearAll,
		pendingDeleteCodes,
		equipmentCodeToId,
	]);

	const handleCancel = useCallback(() => {
		clearAll();
	}, [clearAll]);

	return (
		<FlexRow className="create-site-buttons-row">
			<SkioldButton title={localized('Save')} onPress={handleSubmit} />
			<SkioldButton title={localized('Cancel')} onPress={handleCancel} />
		</FlexRow>
	);
});
